import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import { useVisualizerStore } from "../visualizerNew/useVisualizerStore";
import { useDockSlots } from "./DockSlotsContext";
import { dockStateStore, dockRectStore } from "./dockStore";
import { useDockedVisualizerInput } from "../visualizerNew/useDockedVisualizerInput";
import DockedVisualizerWrapper from "../visualizerNew/DockedVisualizerWrapper";
import { getDesktopNavShiftHeight } from "../visComponents/navigation/navigationConfigTranslate";
import {useAppSelector} from "../../storeHooks";

function getRect(el) {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

function getFullscreenRect() {
  return { top: 0, left: 0, width: window.innerWidth, height: window.innerHeight };
}

// In fullscreen the overlay covers the whole window, so we ignore the slot's
// measured box and just use the viewport dimensions.
function getDockRect(el, dockMode) {
  if (dockMode === "fullscreen") return getFullscreenRect();
  return getRect(el);
}

const TRANSITION =
  "top 500ms ease-in-out, left 500ms ease-in-out, width 500ms ease-in-out, height 500ms ease-in-out, border-radius 500ms ease-in-out";

// Old (pre-dock) canvas markup lived inside `.main` and main.scss shifted it down
// via `.main--nav-opened .canvas { transform: translateY(...) }` whenever the
// desktop navigation menu was open, by different amounts depending on which
// section was active. The docked canvas lives outside `.main` now (this fixed
// shell is a sibling of `.base`), so that selector no longer reaches it — the
// full lookup table is ported to navigationConfigTranslate.ts, and we replicate
// only the shift that applies to whatever section is actually active, driven
// straight off Redux (see getDesktopNavShiftHeight).
const NAV_TRANSITION = "transform 300ms ease-in-out";

/**
 * One fixed element that overlays whichever <DockSlot/> is active and hosts the
 * single, always-mounted <DockedVisualizerWrapper/> (which owns the persistent
 * VisualizerAdapter).
 *
 * Key difference from a naive dock: once the visualizer has mounted, this shell
 * NEVER returns null and never swaps the rendered element — it only moves/resizes
 * the overlay and toggles visibility. That guarantees the visualizer instance is
 * created exactly once and is not remounted on route changes.
 */
export default function VisualizerDockShell() {
  const {slots} = useDockSlots();
  const dockState = useVisualizerStore(dockStateStore.subscribe, dockStateStore.getSnapshot);
  const rect = useVisualizerStore(dockRectStore.subscribe, dockRectStore.getSnapshot);
  const input = useDockedVisualizerInput();
  const {desktopNavigationOpened, activeNavigationParentName, activeNavigationChildName} = useAppSelector(
    (state) => state.navigationState
  );

  // console.log("VisualizerDockShell input", input);

  const slotEl = dockState.activeSlotName ? slots[dockState.activeSlotName] || null : null;

  const frameRef = useRef(0);
  const animateRef = useRef(false);
  const transitionHandledRef = useRef(false);
  const [animateShell, setAnimateShell] = useState(false);

  // Remember the last valid rect so the overlay keeps its box (and the instance
  // stays mounted) even during brief moments when no slot is registered.
  const lastRectRef = useRef(null);
  if (rect) lastRectRef.current = rect;
  const effectiveRect = rect || lastRectRef.current;

  const setRectNextFrame = useCallback((el) => {
    cancelAnimationFrame(frameRef.current);
    frameRef.current = requestAnimationFrame(() => {
      dockRectStore.set(getDockRect(el, dockState.dockMode));
    });
  }, [dockState.dockMode]);

  // Animate on slot / mode change.
  useLayoutEffect(() => {
    if (!slotEl) {
      // Do NOT clear the rect — keep the last box and just hide, so the wrapper
      // (and the single visualizer instance) is never unmounted.
      setAnimateShell(false);
      animateRef.current = false;
      return;
    }
    transitionHandledRef.current = false;
    animateRef.current = true;
    setAnimateShell(true);
    setRectNextFrame(slotEl);
  }, [slotEl, setRectNextFrame]);

  // Resize / scroll / observer: reposition without animating.
  useEffect(() => {
    if (!slotEl) return;
    const updateWithoutAnimation = () => {
      if (animateRef.current) {
        setRectNextFrame(slotEl);
        return;
      }
      setAnimateShell(false);
      setRectNextFrame(slotEl);
    };
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateWithoutAnimation) : null;
    if (ro) ro.observe(slotEl);
    window.addEventListener("resize", updateWithoutAnimation);
    window.addEventListener("scroll", updateWithoutAnimation, true);
    return () => {
      cancelAnimationFrame(frameRef.current);
      if (ro) ro.disconnect();
      window.removeEventListener("resize", updateWithoutAnimation);
      window.removeEventListener("scroll", updateWithoutAnimation, true);
    };
  }, [slotEl, setRectNextFrame]);

  const handleTransitionEnd = (e) => {
    if (!animateRef.current) return;
    if (transitionHandledRef.current) return;
    if (!["top", "left", "width", "height"].includes(e.propertyName)) return;
    transitionHandledRef.current = true;
    animateRef.current = false;
    setAnimateShell(false);
  };

  // console.log("!!!!input", input);
  // console.log("!!!!effectiveRect", effectiveRect);

  // Nothing to show until the first page has fed layers/canvasId in. After that
  // the wrapper stays mounted for the app's lifetime.
  if (!input || !input.canvasId || !effectiveRect) return null;

  const hidden = !slotEl;
  // Only the main visualizer step (fullscreen dock) ever opens the desktop nav —
  // materials/cart (embedded dock) are unaffected either way.
  const navShiftHeight = dockState.dockMode === "fullscreen"
    ? getDesktopNavShiftHeight({
      desktopNavigationOpened,
      activeNavigationParentName,
      activeNavigationChildName,
      visualizerType: document.kvConfig.visualizerType,
    })
    : 0;

  return (
    <div
      onTransitionEnd={handleTransitionEnd}
      className="fixed bg-white overflow-hidden"
      style={{
        zIndex: dockState.dockMode === "fullscreen" ? 15 : 3,
        top: effectiveRect.top,
        left: effectiveRect.left,
        width: effectiveRect.width,
        height: effectiveRect.height,
        // borderRadius: dockState.dockMode === "fullscreen" ? 0 : 24,
        transform: navShiftHeight ? `translateY(${navShiftHeight}px)` : "none",
        transition: animateShell ? `${TRANSITION}, ${NAV_TRANSITION}` : NAV_TRANSITION,
        visibility: hidden ? "hidden" : "visible",
        pointerEvents: hidden ? "none" : "auto",
      }}
    >
      <div style={{position: "relative", width: "100%", height: "100%"}}>
        <DockedVisualizerWrapper
          input={input}
          dockRect={effectiveRect}
          dockMode={dockState.dockMode}
        />
      </div>
    </div>
  )
}
